import { useCallback, useMemo } from 'react';

import { useSelectedColor } from '../useSelectedColor';
import {
  CornerInfoWithProductInfo,
  CustomCutoutWithFullRecord,
  DesignProductMatchingSummary,
  DrawingShapesSummary,
  ProductMatchingReturnParameters,
  ProductMatchingSummary,
  ProductMatchingTargetParameters,
  SelectedCutoutsProductMatchingSummary,
  SelectedItemWithFullRecord,
} from './interfaces/ProductMatchingSummary';
import {
  createCutoutsToSummaryReducer,
  createDrawingShapesToSummaryReducer,
  createDrawingShapesToSummaryReducerUsingApplianceChecker,
  createDrawingShapesToSummaryReducerUsingCornersChecker,
  createPlumbingToSummaryReducer,
  createSelectedItemToSummaryReducer,
  createTearOutsToSummaryReducer,
  getEdgesToReset,
  getFullRecordProductFromCatalog,
  getSideItemTypesToRemove,
  isSelectedItemWithPropertiesAndIsSelected,
} from './utils/useEquivalentProductMatching.utils';

import { CommonProductQueryParams } from '~/api/easy-estimator-api/commonProductQueryParams';
import { useLazyFetchCornerRadiiQuery } from '~/api/easy-estimator-api/corner-radii-api/cornerRadiiService';
import { useCornerRadii } from '~/api/easy-estimator-api/corner-radii-api/hooks/useCornerRadii';
import { useLazyFetchDiagonalCornersQuery } from '~/api/easy-estimator-api/diagonal-corners-api/diagonalCornersService';
import { useDiagonalCorners } from '~/api/easy-estimator-api/diagonal-corners-api/useDiagonalCorners';
import { useLazyFetchEdgesQuery } from '~/api/easy-estimator-api/edges-api/edgesService';
import { selectFilteredEdges } from '~/api/easy-estimator-api/edges-api/useEdges';
import { parseMaterials } from '~/api/easy-estimator-api/materials-api/hooks/useMaterials';
import {
  selectDefaultMaterial,
  useSelectedMaterial,
} from '~/api/easy-estimator-api/materials-api/hooks/useSelectedMaterial';
import { useLazyFetchMaterialsQuery } from '~/api/easy-estimator-api/materials-api/materialsService';
import { useLazyFetchOptionalItemsQuery } from '~/api/easy-estimator-api/optional-items-api/optionalItemsService';
import { useOptionalItems } from '~/api/easy-estimator-api/optional-items-api/useOptionalItems';
import { useLazyFetchPlumbingQuery } from '~/api/easy-estimator-api/plumbing-api/plumbingService';
import { usePlumbing } from '~/api/easy-estimator-api/plumbing-api/usePlumbing';
import {
  useLazyFetchCustomerSuppliedSinksQuery,
  useLazyFetchSinksQuery,
} from '~/api/easy-estimator-api/sinks-api/sinksService';
import { useLazyFetchStoresQuery } from '~/api/easy-estimator-api/stores-api/storesService';
import { useSelectedStore } from '~/api/easy-estimator-api/stores-api/useSelectedStore';
import { useLazyFetchPredefinedCookingAppliancesQuery } from '~/api/local-api/cooking-appliances.api/cookingAppliancesService';
import { drawingTool } from '~/core/engine/DrawingTool';
import { ShapeType } from '~/core/enums';
import i18n from '~/core/i18n/i18n';
import {
  ApplianceModel,
  ApplianceSource,
  ApplianceType,
  ChipMinimizerModel,
  CornerRadiusModel,
  CutoutModel,
  DrawingAppliance,
  DrawingShape,
  EdgeModel,
  OptionalProduct,
  PlumbingProduct,
  SelectedItemMap,
  SelectedItemProperties,
  TearOutModel,
} from '~/core/models';
import { OptionalItemsResponse } from '~/core/models/optional-items-response';
import { CustomCutoutRecord } from '~/core/types/appliances';
import { makeSinksSelector } from '~/core/utils';
import { selectBestMatchingColor } from '~/core/utils/selectBestMatchingColor';
import { isValidZipCode } from '~/core/utils/validate';
import {
  useDrawingShapeContext,
  useOptionalProductContext,
} from '~/legacy/easy-estimator-page/context';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { selectRestorableColor } from '~/redux/slices/color';
import { selectCustomCutouts, setCustomCutouts } from '~/redux/slices/cutout';
import { updateNeedRedrawCanvas } from '~/redux/slices/drawingShape';
import { selectRestorableMaterial } from '~/redux/slices/material';
import {
  selectSelectedOptionalItemsAsSelectedItemMap,
  setOptionalItemsMap,
} from '~/redux/slices/optionalItems';
import {
  selectSelectedPlumbing,
  updateSelectedPlumbing,
} from '~/redux/slices/plumbing';
import {
  clearChipMinimizerFlags,
  selectNumberOfSelectedChipMinimizers,
} from '~/redux/slices/sink';
import { selectRestorableStoreId } from '~/redux/slices/store';
import {
  selectSelectedTearOuts,
  updateSelectedTearOuts,
} from '~/redux/slices/tearOut';
import { selectZipcode } from '~/redux/slices/zipcode';

const emptyArray = [] satisfies DrawingShape[] | SelectedItemWithFullRecord[];

const INITIAL_PRODUCT_MATCHING_SUMMARY = {
  newValueAfterReplacements: emptyArray,
  kept: emptyArray,
  replacedWithEquivalent: emptyArray,
  removed: emptyArray,
} as const;

export const matchingCheckSkipToken = 'MATCHING_CHECK_SKIP_TOKEN' as const;
export type MatchingCheckSkipToken = typeof matchingCheckSkipToken;

export const useEquivalentProductMatching = () => {
  const dispatch = useAppDispatch();
  const {
    getters: { drawingShapes },
    handlers: { updateDrawingShapes },
  } = useDrawingShapeContext();

  const optionalProductContext = useOptionalProductContext();

  const { cornerRadii } = useCornerRadii();
  const { diagonalCorners } = useDiagonalCorners();
  const {
    tearOuts: currentTearOutsCatalog,
    cutouts: currentCutoutsCatalog,
    chipMinimizer: currentChipMinimizerCatalog,
    optionalItems: currentOptionalItemsCatalog,
  } = useOptionalItems();

  const { plumbingItems: currentPlumbingCatalog } = usePlumbing();

  /** RTK lazy fetch method for new catalog */
  const [fetchEdges] = useLazyFetchEdgesQuery();
  const [fetchCornerRadii] = useLazyFetchCornerRadiiQuery();
  const [fetchDiagonalCorners] = useLazyFetchDiagonalCornersQuery();
  const [fetchOptionalItemsQuery] = useLazyFetchOptionalItemsQuery();
  const [fetchStores] = useLazyFetchStoresQuery();
  const [fetchMaterials] = useLazyFetchMaterialsQuery();
  const [fetchSinks] = useLazyFetchSinksQuery();
  const [fetchCustomerSuppliedCookingAppliances] =
    useLazyFetchPredefinedCookingAppliancesQuery();
  const [fetchCustomerSuppliedSinksQuery] =
    useLazyFetchCustomerSuppliedSinksQuery();
  const [fetchPlumbing] = useLazyFetchPlumbingQuery();

  const currentZipCode = useAppSelector(selectZipcode);
  const selectedStore = useSelectedStore();
  const selectedMaterial = useSelectedMaterial();
  const { selectedValue: selectedColor } = useSelectedColor();
  const restorableStoreId = useAppSelector(selectRestorableStoreId);
  const restorableMaterial = useAppSelector(selectRestorableMaterial);
  const restorableColor = useAppSelector(selectRestorableColor);
  const selectedTearOuts = useAppSelector(selectSelectedTearOuts);
  const selectedCutouts = useAppSelector(selectCustomCutouts).selected;
  const selectedPlumbing = useAppSelector(selectSelectedPlumbing);
  const numberOfSelectedChipMinimizers = useAppSelector(
    selectNumberOfSelectedChipMinimizers
  );
  const selectedOptionalItemsAsItemMap = useAppSelector(
    selectSelectedOptionalItemsAsSelectedItemMap
  );

  const selectSinksTransformed = useMemo(() => makeSinksSelector(), []);
  const selectSinksCustomerSuppliedTransformed = useMemo(
    () => makeSinksSelector(),
    []
  );

  /**
   * Fetches all catalog data in parallel
   */
  const fetchAllCatalogData = useCallback(
    async (commonProductQueryParams: CommonProductQueryParams) => {
      const [
        { data: edgesNewCatalog },
        { data: optionalItemsNewCatalog },
        { data: _cornersNewCatalog },
        { data: diagonalCorners },
        { data: _sinksNewCatalog },
        { data: _customerSuppliedSinksNewCatalog },
        { data: cookingAppliancesNewCatalog },
        { data: plumbingNewCatalog },
      ] = await Promise.all([
        fetchEdges(commonProductQueryParams),
        fetchOptionalItemsQuery(commonProductQueryParams),
        fetchCornerRadii(commonProductQueryParams),
        fetchDiagonalCorners(commonProductQueryParams),
        fetchSinks(commonProductQueryParams),
        fetchCustomerSuppliedSinksQuery(commonProductQueryParams),
        fetchCustomerSuppliedCookingAppliances(commonProductQueryParams),
        fetchPlumbing(commonProductQueryParams),
      ]);

      return {
        edgesNewCatalog,
        optionalItemsNewCatalog,
        _cornersNewCatalog,
        diagonalCorners,
        _sinksNewCatalog,
        _customerSuppliedSinksNewCatalog,
        cookingAppliancesNewCatalog,
        plumbingNewCatalog,
      };
    },
    [
      fetchEdges,
      fetchOptionalItemsQuery,
      fetchCornerRadii,
      fetchDiagonalCorners,
      fetchSinks,
      fetchCustomerSuppliedSinksQuery,
      fetchCustomerSuppliedCookingAppliances,
      fetchPlumbing,
    ]
  );

  /**
   * Transforms and combines catalog data into final catalog structure
   */
  const transformCatalogData = useCallback(
    (rawCatalogData: {
      _cornersNewCatalog: CornerRadiusModel[] | undefined;
      diagonalCorners: CornerRadiusModel[] | undefined;
      _sinksNewCatalog: ApplianceModel[] | undefined;
      _customerSuppliedSinksNewCatalog: ApplianceModel[] | undefined;
      cookingAppliancesNewCatalog: ApplianceModel[] | undefined;
      optionalItemsNewCatalog: OptionalItemsResponse | undefined;
    }) => {
      const cornersNewCatalog = [
        ...(rawCatalogData._cornersNewCatalog ?? emptyArray),
        ...(rawCatalogData.diagonalCorners ?? emptyArray),
      ];

      const customerSuppliedSinksNewCatalog =
        selectSinksCustomerSuppliedTransformed(
          rawCatalogData._customerSuppliedSinksNewCatalog ?? emptyArray,
          ApplianceSource.PROGRAM,
          ApplianceType.SINK_LABOR
        );

      const sinksNewCatalog = selectSinksTransformed(
        rawCatalogData._sinksNewCatalog ?? emptyArray,
        ApplianceSource.PROGRAM,
        ApplianceType.SINK,
        true
      );

      const appliancesNewCatalog: ApplianceModel[] = [
        ...(sinksNewCatalog ?? emptyArray),
        ...(customerSuppliedSinksNewCatalog ?? emptyArray),
        ...(rawCatalogData.cookingAppliancesNewCatalog ?? emptyArray),
      ];

      const cutoutsNewCatalog =
        rawCatalogData.optionalItemsNewCatalog?.cutouts ?? emptyArray;
      const tearOutsNewCatalog =
        rawCatalogData.optionalItemsNewCatalog?.['tear-outs'] ?? emptyArray;

      const optionalItemsNewCatalog =
        rawCatalogData.optionalItemsNewCatalog?.others ?? emptyArray;

      return {
        cornersNewCatalog,
        appliancesNewCatalog,
        tearOutsNewCatalog,
        cutoutsNewCatalog,
        optionalItemsNewCatalog,
      };
    },
    [selectSinksCustomerSuppliedTransformed, selectSinksTransformed]
  );

  /**
   * Given one or more target parameters for zipCode, Store, Material, Color, fetch and select the appropriate targets
   * Any parameters not provided default to the appropriate current selection
   * Any parameters (specified target or current) that are no longer valid (i.e. store not in new zip code, material not available in new store)
   *  results in an appropriate default selection
   * @returns {undefined} if failing to fetch stores or materials. Otherwise, returns CommonProductQueryParams with validated values
   */
  const resolveProductMatchingParams = useCallback(
    async (
      args: ProductMatchingTargetParameters
    ): Promise<ProductMatchingReturnParameters | undefined> => {
      // use any of the given values OR the currently selected values
      const {
        zipCode = currentZipCode,
        storeId = selectedStore?.companyId,
        material: intendedMaterial = {
          description: selectedMaterial?.description,
        },
        color = {
          productId: selectedColor?.productId,
          masterSku: selectedColor?.masterSku,
          thickness: selectedColor?.thickness,
          name: selectedColor?.name,
          brand: selectedColor?.brand,
        },
      } = args;

      if (!isValidZipCode(zipCode)) {
        return undefined;
      }

      // fetch stores for the new zip. For the old it won’t fetch from the API because of RTK query cache
      const rawStores = await fetchStores({ params: { zipCode } }, true);

      if (rawStores.isError || !rawStores?.data?.length) {
        console.warn(`Error fetching stores for zip code ${zipCode}`);

        return undefined;
      }

      // and ensure the target store is in there, or select the default store for the new zip code
      const newStore =
        rawStores.data.find(
          (store) =>
            (storeId && store.companyId === storeId) ||
            (restorableStoreId && store.companyId === restorableStoreId)
        ) ?? rawStores.data[0];

      // fetch materials based on the validated zip and store
      const rawMaterials = await fetchMaterials(
        { params: { zipCode, storeId: newStore.companyId } },
        true
      );

      if (rawMaterials.isError || !rawMaterials?.data?.length) {
        console.warn(
          `Error fetching materials for zip code ${zipCode} and store ${newStore.companyId}`
        );

        return undefined;
      }

      //this is primarily so we're working with MaterialModel records
      const materials = parseMaterials(rawMaterials.data);

      // and try to find the target material (if changing materials), or the currently userSelectedMaterial currently exists
      // or select default material if not
      const matchingMaterial =
        materials?.find(
          ({ description }) =>
            intendedMaterial && description === intendedMaterial.description
        ) ??
        materials?.find(
          ({ description }) =>
            restorableMaterial && description === restorableMaterial.description
        ) ??
        selectDefaultMaterial({
          materials,
          selectedValue: selectedMaterial,
        });

      // colors come from whatever material we just found
      const colors = matchingMaterial?.colors ?? emptyArray;

      // find the best match for the given color, or the selectedColor
      const newColor = selectBestMatchingColor(color, colors, restorableColor);

      if (!newColor) {
        console.warn(
          `Error fetching colors for zip code ${zipCode}, store ${newStore?.companyId} and material ${matchingMaterial?.description}`
        );

        return undefined;
      }

      // now return all the _validated_ values - all subsequent api lookups should be from these
      return {
        zipCode,
        storeId: newStore.companyId,
        store: newStore,
        prodQteGrpID: newColor.qteGrpID,
        material: matchingMaterial,
        color: newColor,
      };
    },
    [
      currentZipCode,
      selectedStore?.companyId,
      selectedMaterial,
      selectedColor?.productId,
      selectedColor?.masterSku,
      selectedColor?.thickness,
      selectedColor?.name,
      selectedColor?.brand,
      fetchStores,
      fetchMaterials,
      restorableColor,
      restorableStoreId,
      restorableMaterial,
    ]
  );

  const fetchNewCatalogs = useCallback(
    async (
      commonProductQueryParams: CommonProductQueryParams
    ): Promise<{
      edgesNewCatalog: EdgeModel[] | undefined;
      cornersNewCatalog: CornerRadiusModel[] | undefined;
      appliancesNewCatalog: ApplianceModel[];
      chipMinimizersNewCatalog?: ChipMinimizerModel;
      cutoutsNewCatalog: CutoutModel[];
      tearOutsNewCatalog: TearOutModel[];
      optionalItemsNewCatalog: OptionalProduct[];
      plumbingNewCatalog: PlumbingProduct[] | undefined;
    }> => {
      const rawCatalogData = await fetchAllCatalogData(
        commonProductQueryParams
      );
      const transformedData = transformCatalogData(rawCatalogData);

      return {
        edgesNewCatalog: rawCatalogData.edgesNewCatalog,
        cornersNewCatalog: transformedData.cornersNewCatalog,
        appliancesNewCatalog: transformedData.appliancesNewCatalog,
        chipMinimizersNewCatalog:
          rawCatalogData.optionalItemsNewCatalog?.['chip-minimizer'],
        cutoutsNewCatalog: transformedData.cutoutsNewCatalog,
        tearOutsNewCatalog: transformedData.tearOutsNewCatalog,
        optionalItemsNewCatalog: transformedData.optionalItemsNewCatalog,
        plumbingNewCatalog: rawCatalogData.plumbingNewCatalog,
      };
    },
    [fetchAllCatalogData, transformCatalogData]
  );

  /**
   * Calculates product matching summary for edges
   */
  const calculateEdgeSummary = useCallback(
    (
      drawingShapes: DrawingShape[],
      edgesNewCatalog: EdgeModel[],
      newColorBrand?: string | null
    ): ProductMatchingSummary<EdgeModel, DrawingShape> => {
      const clonedDrawingShapes = drawingTool.cloneShapes(drawingShapes);

      return drawingShapes.reduce(
        createDrawingShapesToSummaryReducer(edgesNewCatalog, newColorBrand),
        {
          ...INITIAL_PRODUCT_MATCHING_SUMMARY,
          newValueAfterReplacements: clonedDrawingShapes,
        }
      );
    },
    []
  );

  /**
   * Calculates product matching summary for appliances
   */
  const calculateApplianceSummary = useCallback(
    (
      shapesAfterEdgeCheck: DrawingShape[],
      appliancesNewCatalog: ApplianceModel[]
    ): ProductMatchingSummary<DrawingAppliance, DrawingShape> =>
      shapesAfterEdgeCheck.reduce(
        createDrawingShapesToSummaryReducerUsingApplianceChecker(
          appliancesNewCatalog
        ),
        {
          ...INITIAL_PRODUCT_MATCHING_SUMMARY,
          newValueAfterReplacements:
            drawingTool.cloneShapes(shapesAfterEdgeCheck), // Since checker mutate array that is reduced we need to clone initial array
        }
      ),
    []
  );

  /**
   * Calculates product matching summary for corners
   */
  const calculateCornerSummary = useCallback(
    (
      shapesAfterApplianceCheck: DrawingShape[],
      cornersNewCatalog: CornerRadiusModel[],
      currentCornersCatalog: CornerRadiusModel[]
    ): ProductMatchingSummary<CornerInfoWithProductInfo, DrawingShape> =>
      shapesAfterApplianceCheck.reduce(
        createDrawingShapesToSummaryReducerUsingCornersChecker(
          cornersNewCatalog,
          currentCornersCatalog
        ),
        {
          ...INITIAL_PRODUCT_MATCHING_SUMMARY,
          newValueAfterReplacements: drawingTool.cloneShapes(
            shapesAfterApplianceCheck
          ), // Since checker mutate array that is reduced we need to clone initial array
        }
      ),
    []
  );

  /**
   * Aggregates all product matching summaries into a single summary
   */
  const aggregateSummaries = useCallback(
    ({
      edgeSummary,
      applianceSummary,
      cornerSummary,
      originalDrawingShapes,
    }: {
      edgeSummary: ProductMatchingSummary<EdgeModel, DrawingShape>;
      applianceSummary: ProductMatchingSummary<DrawingAppliance, DrawingShape>;
      cornerSummary: ProductMatchingSummary<
        CornerInfoWithProductInfo,
        DrawingShape
      >;
      originalDrawingShapes: DrawingShape[];
    }): DrawingShapesSummary => ({
      newValueAfterReplacements: cornerSummary.newValueAfterReplacements,
      valueBeforeReplacements: drawingTool.cloneShapes(originalDrawingShapes),
      kept: [
        ...edgeSummary.kept,
        ...applianceSummary.kept,
        ...cornerSummary.kept,
      ],
      replacedWithEquivalent: [
        ...edgeSummary.replacedWithEquivalent,
        ...applianceSummary.replacedWithEquivalent,
        ...cornerSummary.replacedWithEquivalent,
      ],
      removed: [
        ...edgeSummary.removed,
        ...applianceSummary.removed,
        ...cornerSummary.removed,
      ],
    }),
    []
  );

  const calculateSummaryFromDrawingShapes = useCallback(
    ({
      drawingShapes,
      newCatalogs: { edgesNewCatalog, appliancesNewCatalog, cornersNewCatalog },
      newColorBrand,
    }: {
      drawingShapes: DrawingShape[];
      newCatalogs: {
        edgesNewCatalog: EdgeModel[] | undefined;
        appliancesNewCatalog: ApplianceModel[];
        cornersNewCatalog: CornerRadiusModel[] | undefined;
      };
      newColorBrand?: string | null;
    }): { summaryFromDrawingShapes: DrawingShapesSummary } => {
      const edgeSummary = calculateEdgeSummary(
        drawingTool.cloneShapesWithoutFaux(drawingShapes),
        edgesNewCatalog ?? emptyArray,
        newColorBrand
      );

      const applianceSummary = calculateApplianceSummary(
        edgeSummary.newValueAfterReplacements,
        appliancesNewCatalog
      );

      const cornerSummary = calculateCornerSummary(
        applianceSummary.newValueAfterReplacements,
        cornersNewCatalog ?? emptyArray,
        [...cornerRadii, ...diagonalCorners]
      );

      const summaryFromDrawingShapes = aggregateSummaries({
        edgeSummary,
        applianceSummary,
        cornerSummary,
        originalDrawingShapes: drawingShapes,
      });

      return { summaryFromDrawingShapes };
    },
    [
      cornerRadii,
      diagonalCorners,
      calculateEdgeSummary,
      calculateApplianceSummary,
      calculateCornerSummary,
      aggregateSummaries,
    ]
  );

  const calculateSummaryFromTearOuts = useCallback(
    ({
      selectedTearOuts,
      tearOutsNewCatalog,
      currentTearOutsCatalog,
    }: {
      selectedTearOuts: Record<number, number>;
      tearOutsNewCatalog: TearOutModel[];
      currentTearOutsCatalog: TearOutModel[];
    }): ProductMatchingSummary<
      SelectedItemWithFullRecord,
      SelectedItemWithFullRecord
    > => {
      // Convert selected tear-outs to SelectedItemWithFullRecord[]
      const selectedTearOutsWithFullRecord = Object.entries(selectedTearOuts)
        .map(([productId, qty]): [number, SelectedItemProperties] => [
          Number(productId),
          { quantity: qty, isSelected: true, isManuallyChanged: true },
        ])
        .map(getFullRecordProductFromCatalog(currentTearOutsCatalog))
        .filter(isSelectedItemWithPropertiesAndIsSelected);

      return selectedTearOutsWithFullRecord.reduce(
        createTearOutsToSummaryReducer(tearOutsNewCatalog),
        {
          ...INITIAL_PRODUCT_MATCHING_SUMMARY,
          newValueAfterReplacements: [...selectedTearOutsWithFullRecord],
        }
      );
    },
    []
  );

  const calculateSummaryFromCutouts = useCallback(
    ({
      selectedCutouts,
      cutoutsNewCatalog,
      currentCutoutsCatalog,
    }: {
      selectedCutouts: CustomCutoutRecord;
      cutoutsNewCatalog: CutoutModel[];
      currentCutoutsCatalog: CutoutModel[];
    }): SelectedCutoutsProductMatchingSummary => {
      const selectedCutoutsEntries = Object.entries(selectedCutouts).filter(
        ([, details]) => {
          const { onCard = 0, onShape = 0 } = details;

          return onCard + onShape > 0;
        }
      );

      const selectedCutoutsWithFullRecord = selectedCutoutsEntries
        .map(
          ([cutoutId, cutoutDetails]):
            | CustomCutoutWithFullRecord
            | undefined => {
            const cutout = currentCutoutsCatalog.find(
              (catalogCutout) => catalogCutout.productId === +cutoutId
            );

            return cutout
              ? {
                  cutout,
                  ...cutoutDetails,
                }
              : undefined;
          }
        )
        .filter(
          (
            cutoutWithFullRecord
          ): cutoutWithFullRecord is CustomCutoutWithFullRecord =>
            !!cutoutWithFullRecord
        ) as CustomCutoutWithFullRecord[];

      // Process each cutout through the checker
      return selectedCutoutsWithFullRecord.reduce(
        createCutoutsToSummaryReducer(cutoutsNewCatalog),
        {
          ...INITIAL_PRODUCT_MATCHING_SUMMARY,
          newValueAfterReplacements: [...selectedCutoutsWithFullRecord],
        }
      );
    },
    []
  );

  const calculateSummaryFromChipMinimizers = useCallback(
    ({
      chipMinimizersNewCatalog: newChipMin,
      currentChipMinimizerCatalog: currentChipMin,
    }: {
      chipMinimizersNewCatalog?: ChipMinimizerModel;
      currentChipMinimizerCatalog?: ChipMinimizerModel;
    }): ProductMatchingSummary<
      SelectedItemWithFullRecord,
      SelectedItemWithFullRecord
    > => {
      // Determine total selected chip minimizers: in drawing + in side panel counters
      const totalInDrawing = drawingShapes.reduce((acc, shape) => {
        shape.appliances.forEach((appliance) => {
          if (appliance.hasChipMinimizer) {
            acc += 1;
          }
        });

        return acc;
      }, 0);

      // Count from side panel counters via selector gathered at hook level
      const totalSelected = totalInDrawing + numberOfSelectedChipMinimizers;

      // If nothing selected, summary is empty
      if (!totalSelected) {
        return {
          ...INITIAL_PRODUCT_MATCHING_SUMMARY,
          newValueAfterReplacements: [],
        };
      }

      // Build a single SelectedItemWithFullRecord reflecting total quantity
      const selectedItem: SelectedItemWithFullRecord | undefined =
        currentChipMin
          ? {
              selectedItem: currentChipMin,
              selectedItemProperties: {
                quantity: totalSelected,
                isSelected: true,
                isManuallyChanged: true,
              },
            }
          : undefined;

      // If the same chip minimizer is available, keep it; otherwise mark all for removal
      if (
        currentChipMin &&
        newChipMin &&
        newChipMin.productId === currentChipMin.productId
      ) {
        return {
          ...INITIAL_PRODUCT_MATCHING_SUMMARY,
          kept: selectedItem ? [selectedItem] : [],
          newValueAfterReplacements: selectedItem ? [selectedItem] : [],
        };
      }

      return {
        ...INITIAL_PRODUCT_MATCHING_SUMMARY,
        removed: selectedItem ? [selectedItem] : [],
        newValueAfterReplacements: [],
      };
    },
    [drawingShapes, numberOfSelectedChipMinimizers]
  );

  const calculateSummaryFromOptionalProducts = useCallback(
    ({
      selectedOptionalProducts,
      optionalProductsNewCatalog,
      currentOptionalProductsCatalog,
    }: {
      selectedOptionalProducts: SelectedItemMap;
      optionalProductsNewCatalog: PlumbingProduct[];
      currentOptionalProductsCatalog: OptionalProduct[];
    }): ProductMatchingSummary<
      SelectedItemWithFullRecord,
      SelectedItemWithFullRecord
    > =>
      Object.entries(selectedOptionalProducts)
        .map<
          SelectedItemWithFullRecord | undefined
        >(getFullRecordProductFromCatalog(currentOptionalProductsCatalog))
        .filter(isSelectedItemWithPropertiesAndIsSelected)
        .reduce<
          ProductMatchingSummary<
            SelectedItemWithFullRecord,
            SelectedItemWithFullRecord
          >
        >(createSelectedItemToSummaryReducer(optionalProductsNewCatalog), INITIAL_PRODUCT_MATCHING_SUMMARY),
    []
  );

  const calculateSummaryFromPlumbing = useCallback(
    ({
      selectedPlumbing,
      plumbingNewCatalog,
      currentPlumbingCatalog,
    }: {
      selectedPlumbing: Record<number, SelectedItemProperties>;
      plumbingNewCatalog: PlumbingProduct[];
      currentPlumbingCatalog: PlumbingProduct[];
    }): ProductMatchingSummary<
      SelectedItemWithFullRecord,
      SelectedItemWithFullRecord
    > => {
      // Convert selected plumbing to SelectedItemWithFullRecord[]
      const selectedPlumbingWithFullRecord = Object.entries(selectedPlumbing)
        .map(getFullRecordProductFromCatalog(currentPlumbingCatalog))
        .filter(
          isSelectedItemWithPropertiesAndIsSelected
        ) as SelectedItemWithFullRecord[];

      return selectedPlumbingWithFullRecord.reduce(
        createPlumbingToSummaryReducer(plumbingNewCatalog),
        {
          ...INITIAL_PRODUCT_MATCHING_SUMMARY,
          newValueAfterReplacements: [...selectedPlumbingWithFullRecord],
        }
      );
    },
    []
  );

  /**
   * Makes the current design (defined as the drawingShapes and selectedItems (cutouts, tear-outs,etc.))
   * against a new catalog that is built from the args (zipCode, storeId, qteGrpId)
   *
   * @returns {ProductMatchingSummary}
   * */
  const makeProductMatchingSummary = useCallback(
    async ({
      zipCode,
      storeId,
      prodQteGrpID: qteGrpID,
      material,
      color,
    }: ProductMatchingTargetParameters &
      Required<
        Required<CommonProductQueryParams>['params']
      >): Promise<DesignProductMatchingSummary> => {
      const commonProductQueryParams: CommonProductQueryParams = {
        params: { zipCode, storeId, prodQteGrpID: qteGrpID },
      };

      //Fetch new catalog for param candidates (zipCode, storeId and prodQteGrpID)
      const {
        edgesNewCatalog,
        cornersNewCatalog,
        appliancesNewCatalog,
        chipMinimizersNewCatalog,
        cutoutsNewCatalog,
        tearOutsNewCatalog,
        plumbingNewCatalog,
        optionalItemsNewCatalog,
      } = await fetchNewCatalogs(commonProductQueryParams);

      const filteredEdges = selectFilteredEdges(
        edgesNewCatalog ?? emptyArray,
        material,
        color
      );

      // Summary from drawingShapes
      const { summaryFromDrawingShapes } = calculateSummaryFromDrawingShapes({
        drawingShapes,
        newCatalogs: {
          edgesNewCatalog: filteredEdges,
          appliancesNewCatalog,
          cornersNewCatalog,
        },
        newColorBrand: color?.brand,
      });

      const summaryFromOptionalProducts = calculateSummaryFromOptionalProducts({
        selectedOptionalProducts: selectedOptionalItemsAsItemMap,
        currentOptionalProductsCatalog: currentOptionalItemsCatalog,
        optionalProductsNewCatalog: optionalItemsNewCatalog,
      });

      const summaryForPlumbingSelectedItems = calculateSummaryFromPlumbing({
        selectedPlumbing,
        plumbingNewCatalog: plumbingNewCatalog ?? emptyArray,
        currentPlumbingCatalog: currentPlumbingCatalog ?? emptyArray,
      });

      const summaryFromCutouts = calculateSummaryFromCutouts({
        selectedCutouts,
        cutoutsNewCatalog: cutoutsNewCatalog ?? emptyArray,
        currentCutoutsCatalog: currentCutoutsCatalog ?? emptyArray,
      });

      const summaryFromTearOuts = calculateSummaryFromTearOuts({
        selectedTearOuts,
        tearOutsNewCatalog: tearOutsNewCatalog ?? emptyArray,
        currentTearOutsCatalog: currentTearOutsCatalog ?? emptyArray,
      });

      const summaryFromChipMinimizers = calculateSummaryFromChipMinimizers({
        chipMinimizersNewCatalog,
        currentChipMinimizerCatalog,
      });

      return {
        drawingShapes: summaryFromDrawingShapes,
        cutouts: summaryFromCutouts,
        tearOuts: summaryFromTearOuts,
        plumbing: summaryForPlumbingSelectedItems,
        chipMinimizers: summaryFromChipMinimizers,
        selectedOptionalProducts: summaryFromOptionalProducts,
      };
    },
    [
      fetchNewCatalogs,
      calculateSummaryFromDrawingShapes,
      drawingShapes,
      calculateSummaryFromOptionalProducts,
      selectedOptionalItemsAsItemMap,
      currentOptionalItemsCatalog,
      calculateSummaryFromPlumbing,
      selectedPlumbing,
      currentPlumbingCatalog,
      calculateSummaryFromCutouts,
      selectedCutouts,
      currentCutoutsCatalog,
      calculateSummaryFromTearOuts,
      selectedTearOuts,
      currentTearOutsCatalog,
      calculateSummaryFromChipMinimizers,
      currentChipMinimizerCatalog,
    ]
  );

  /**
   * Runs an equivalentProductMatchingCheck against a new catalog defined by args
   * If some args are undefined. These are inferred using defaults
   * Does not update the design
   * @returns {DesignProductMatchingSummary | null} if failing to get commonProductQueryParams
   */
  const resolveAndMakeProductMatchingSummary = useCallback(
    async (
      args: ProductMatchingTargetParameters
    ): Promise<DesignProductMatchingSummary | null> => {
      const validatedQueryParams = await resolveProductMatchingParams(args);

      return validatedQueryParams
        ? await makeProductMatchingSummary(validatedQueryParams)
        : null;
    },
    [makeProductMatchingSummary, resolveProductMatchingParams]
  );

  const clearInvalidSideItems = useCallback(
    (
      newShapes: DrawingShape[],
      originalShapes: DrawingShape[],
      materialDescription?: string
    ) => {
      if (newShapes.length === 0 || !materialDescription) {
        return;
      }

      const typesToRemove = getSideItemTypesToRemove(materialDescription);

      const shapesToProcess = originalShapes.filter((shape) =>
        newShapes.some(
          (newShape) => newShape.canvasObjectName === shape.canvasObjectName
        )
      );

      const edgesToReset = getEdgesToReset(shapesToProcess, typesToRemove);

      newShapes.forEach((shape) => {
        const resetsForThisShape = edgesToReset.filter(
          (reset) => reset.shapeName === shape.canvasObjectName
        );

        if (!shape.arrowsInfo || resetsForThisShape.length === 0) {
          return;
        }

        shape.arrowsInfo.forEach((arrow) => {
          const reset = resetsForThisShape.find(
            (item) => item.edgeName === arrow.label
          );

          if (reset) {
            arrow.isActiveForEdgeCalc = true;
            arrow.isSideItemPresent = false;
            arrow.isVisibleInCanvas = true;
          }
        });

        shape.drawingEdges.forEach((drawingEdge) => {
          const reset = resetsForThisShape.find(
            (item) => item.sideItem.arrowInfo?.drawingEdgeId === drawingEdge.id
          );

          if (reset) {
            drawingEdge.isVisibleInCanvas = true;
            drawingEdge.sideItem.type = undefined; // TODO EZE-1448
          }
        });

        if (shape.removedElementsHistory?.edgesAndCorners) {
          shape.removedElementsHistory.edgesAndCorners =
            shape.removedElementsHistory.edgesAndCorners.filter(
              (removedEdge) =>
                !resetsForThisShape.some(
                  (item) =>
                    item.sideItem.arrowInfo?.drawingEdgeId ===
                    removedEdge.drawingEdge.id
                )
            );
        }
      });
    },
    []
  );

  /**
   * Updates design with the new values after an equivalentProductMatchingCheck
   * where each product is checked against the new catalog.
   * The result is a summary that contains an array of the products kept, replacedWithEquivalent, the removed, and  the new design.
   * If failing to get commonProductQueryParams, this function does nothing.
   * @returns The updated shapes array and validated params (including matched color), or undefined if the operation failed
   * */
  const replaceProductsWithEquivalents = async (
    args: ProductMatchingTargetParameters
  ): Promise<
    | {
        shapes: DrawingShape[];
        validatedParams: ProductMatchingReturnParameters;
      }
    | undefined
  > => {
    const validParams = await resolveProductMatchingParams(args);

    if (!validParams) {
      return undefined;
    }

    const summaries = await makeProductMatchingSummary(validParams);

    // If chip minimizer becomes unavailable, clear flags in sinks and drawing
    if (
      summaries.chipMinimizers &&
      summaries.chipMinimizers.removed.length > 0
    ) {
      // Clear flags from side panel counters
      dispatch(clearChipMinimizerFlags());

      // Clear flags from in-drawing appliances
      const shapesClone = drawingTool.cloneShapes(
        summaries.drawingShapes.newValueAfterReplacements
      );

      shapesClone.forEach((shape) => {
        shape.appliances.forEach((appliance) => {
          appliance.hasChipMinimizer = false;
        });
      });
      updateDrawingShapes(shapesClone);
    }

    // Corners equivalent replacement is handled automatically in calculateSummaryFromDrawingShapes
    // via createDrawingShapesToSummaryReducerUsingCornersChecker. The replaced corners are included
    // in summaries.drawingShapes.newValueAfterReplacements and will be applied
    // when updateDrawingShapes is called below.

    clearInvalidSideItems(
      summaries.drawingShapes.newValueAfterReplacements,
      drawingShapes,
      args.material?.description ?? undefined
    );

    const newMaterial = args.material?.description;
    const typesToRemove = getSideItemTypesToRemove(newMaterial);
    const edgesToReset = getEdgesToReset(drawingShapes, typesToRemove);

    if (edgesToReset) {
      // update drawingShapes with all the resetEdges values
      summaries.drawingShapes.newValueAfterReplacements.forEach((shape) => {
        const resetsForThisShape = edgesToReset.filter(
          (reset) => reset.shapeName === shape.canvasObjectName
        );

        if (!!shape.arrowsInfo && resetsForThisShape.length) {
          shape.arrowsInfo.forEach((arrow) => {
            const reset = resetsForThisShape.find(
              (item) => item.edgeName === arrow.label
            );

            if (reset) {
              arrow.isActiveForEdgeCalc = true;
              arrow.isSideItemPresent = false;
              arrow.isVisibleInCanvas = true;
            }
          });

          shape.drawingEdges.forEach((drawingEdge) => {
            const reset = resetsForThisShape.find(
              (item) =>
                item.sideItem?.arrowInfo?.drawingEdgeId === drawingEdge.id
            );

            if (reset) {
              drawingEdge.isVisibleInCanvas = true;
              drawingEdge.sideItem.type = undefined;
            }
          });

          if (shape.removedElementsHistory?.edgesAndCorners) {
            shape.removedElementsHistory.edgesAndCorners =
              shape.removedElementsHistory?.edgesAndCorners.filter(
                (removedEdge) =>
                  !resetsForThisShape.some(
                    (item) =>
                      item.sideItem?.arrowInfo?.drawingEdgeId ===
                      removedEdge.drawingEdge.id
                  )
              );
          }
        }
      });
    }

    // Update plumbing with replacements
    if (summaries.plumbing) {
      const newPlumbingMap =
        summaries.plumbing.newValueAfterReplacements.reduce<
          Record<number, SelectedItemProperties>
        >((acc, item) => {
          const plumbing = item.selectedItem as PlumbingProduct;

          acc[plumbing.productId] = item.selectedItemProperties;

          return acc;
        }, {});

      dispatch(updateSelectedPlumbing(newPlumbingMap));
    }

    // optional products
    optionalProductContext?.resetRestorableOptionalProducts();

    //update cutouts with replacements
    if (summaries.cutouts) {
      const newCutoutsMap =
        summaries.cutouts.newValueAfterReplacements.reduce<CustomCutoutRecord>(
          (acc, item) => {
            const cutout = item.cutout;

            acc[cutout.productId] = item;

            return acc;
          },
          {} as CustomCutoutRecord
        );

      dispatch(setCustomCutouts({ selected: newCutoutsMap }));
    }

    if (summaries.selectedOptionalProducts) {
      const newOptionalProductsMap =
        summaries.selectedOptionalProducts.newValueAfterReplacements.reduce<
          Record<number, number>
        >((acc, item) => {
          const optionalProduct = item.selectedItem;

          acc[optionalProduct.productId] = item.selectedItemProperties.quantity;

          return acc;
        }, {});

      dispatch(setOptionalItemsMap(newOptionalProductsMap));
    }

    // Update tear-outs with replacements
    if (summaries.tearOuts) {
      const newTearOutsMap =
        summaries.tearOuts.newValueAfterReplacements.reduce<
          Record<number, number>
        >((acc, item) => {
          const tearOut = item.selectedItem as TearOutModel;

          acc[tearOut.productId] = item.selectedItemProperties.quantity;

          return acc;
        }, {});

      dispatch(updateSelectedTearOuts(newTearOutsMap));
    }

    //update drawingShapes with summaries
    const shapesToUpdate = drawingTool.cloneShapes([
      ...drawingShapes.filter((shape) => shape.type === ShapeType.FAUX_SHAPE),
      ...summaries.drawingShapes.newValueAfterReplacements,
    ]);

    updateDrawingShapes(shapesToUpdate);

    dispatch(updateNeedRedrawCanvas(true));

    return {
      shapes: shapesToUpdate,
      validatedParams: validParams,
    };
  };

  type AlertWithMetadata = {
    label: string;
    imageUrl?: string;
    userMustCancel?: boolean;
  };

  const getAlerts = useCallback(
    async (
      args: ProductMatchingTargetParameters
    ): Promise<AlertWithMetadata[]> => {
      const resolvedParams = await resolveProductMatchingParams(args);

      if (resolvedParams) {
        return [];
      }

      const zipMessage = args?.zipCode
        ? i18n.t('EQUIVALENT_PRODUCT_MATCHING.ZIP_CODE.ALERT_TEXT', {
            zipCode: args.zipCode,
          })
        : undefined;

      const storeMessage = args?.storeId
        ? i18n.t('EQUIVALENT_PRODUCT_MATCHING.STORE.ALERT_TEXT', {
            storeId: args.storeId,
          })
        : undefined;

      return [
        {
          label: storeMessage ?? zipMessage ?? 'An error has occurred',
          userMustCancel: true,
        },
      ];
    },
    [resolveProductMatchingParams]
  );

  return {
    replaceProductsWithEquivalents,
    resolveAndMakeProductMatchingSummary,
    getAlerts,
  };
};
